import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { useAuth } from "../../context/AuthContext";
import {
  fetchBillsForCustomer,
  fetchPaymentsForCustomer,
  fetchAuditNotesForCustomer,
  creditBillBranch,
  creditPaymentBranch,
  paymentTimestamp,
} from "../../services/creditDueService";
import { useCreditBranchFilter } from "./useCreditBranchFilter";
import CreditBranchSelector from "./CreditBranchSelector";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default function CreditCustomerTimelinePage() {
  const { customerId } = useParams();
  const { user: storedUser } = useAuth();
  const { branch, setBranch, visibleBranches } = useCreditBranchFilter(storedUser);

  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setErr("");
    try {
      const [rows, payRows, noteRows] = await Promise.all([
        fetchBillsForCustomer(customerId),
        fetchPaymentsForCustomer(customerId),
        fetchAuditNotesForCustomer(customerId),
      ]);
      setBills(rows);
      setPayments(payRows);
      setNotes(noteRows);
    } catch (e) {
      setErr(e?.message || "Failed to load timeline.");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const billById = useMemo(() => {
    const m = new Map();
    for (const b of bills) {
      if (b?.id) m.set(b.id, b);
    }
    return m;
  }, [bills]);

  const billsBranch = useMemo(
    () => bills.filter((b) => creditBillBranch(b) === branch),
    [bills, branch]
  );

  const paymentsBranch = useMemo(
    () => payments.filter((p) => creditPaymentBranch(p, billById) === branch),
    [payments, billById, branch]
  );

  const name = bills[0]?.customerName || billsBranch[0]?.customerName || "Customer";

  const timeline = useMemo(() => {
    const fromBills = billsBranch.map((b) => ({
      key: `bill_${b.id}`,
      date:
        b.createdAt?.toDate?.() ||
        (typeof b.createdAt?.seconds === "number" ? new Date(b.createdAt.seconds * 1000) : new Date(0)),
      type: b.manual ? "adjustment" : "invoice",
      title: b.manual ? "Manual credit entry" : "Credit invoice",
      detail: `${b.voucherNo || b.id} · Balance ₹${round2(b.balanceAmount).toLocaleString("en-IN")}`,
      amount: round2(b.totalAmount),
    }));

    const fromPayments = paymentsBranch.map((p) => ({
      key: `pay_${p.id}`,
      date: paymentTimestamp(p),
      type: "payment",
      title: "Payment received",
      detail: `${(p.method || "manual").toUpperCase()} · ${(p.payMode || "cash").toUpperCase()}`,
      amount: -round2(p.amount),
    }));

    const fromNotes = notes.map((n) => ({
      key: `note_${n.id}`,
      date:
        n.createdAt?.toDate?.() ||
        (typeof n.createdAt?.seconds === "number" ? new Date(n.createdAt.seconds * 1000) : new Date(0)),
      type: "note",
      title: "Audit note",
      detail: n.note || "",
      amount: null,
      lastContactDate: n.lastContactDate || "",
      nextActionDate: n.nextActionDate || "",
    }));

    return [...fromBills, ...fromPayments, ...fromNotes].sort((a, b) => b.date - a.date);
  }, [billsBranch, paymentsBranch, notes]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-5xl px-3 pb-24 pt-4 sm:px-4">
        <div className="mb-4">
          <Link to={`/credit-due/customer/${encodeURIComponent(customerId)}`} className="text-sm font-semibold text-indigo-600 hover:underline">
            ← Back to party details
          </Link>
          <h1 className="mt-2 text-lg font-bold text-slate-900">Customer timeline</h1>
          <p className="text-sm font-semibold text-slate-800">{name}</p>
          <p className="text-xs font-mono text-slate-500">{customerId}</p>
          <div className="mt-3 max-w-xs">
            <CreditBranchSelector value={branch} onChange={setBranch} options={visibleBranches} />
          </div>
        </div>

        {err ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>
        ) : null}

        {loading ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : timeline.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600 shadow-sm">
            No timeline events yet.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-xs font-bold uppercase text-slate-600">{timeline.length} events</p>
            </div>
            <ul className="divide-y divide-slate-100">
              {timeline.map((e) => (
                <li key={e.key} className="p-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">
                        {e.title}{" "}
                        <span className="text-xs font-medium uppercase text-slate-500">({e.type})</span>
                      </p>
                      <p className="mt-0.5 text-xs text-slate-600 break-words">{e.detail || "—"}</p>
                      {e.lastContactDate || e.nextActionDate ? (
                        <p className="mt-1 text-[11px] text-slate-500">
                          Last: {e.lastContactDate || "—"} · Next: {e.nextActionDate || "—"}
                        </p>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-left sm:text-right">
                      <p className="text-xs text-slate-500">{e.date?.toLocaleDateString("en-IN") || "—"}</p>
                      {typeof e.amount === "number" ? (
                        <p className={`mt-0.5 text-sm font-bold ${e.amount < 0 ? "text-emerald-700" : "text-slate-900"}`}>
                          {e.amount < 0 ? "-" : "+"}₹{Math.abs(round2(e.amount)).toLocaleString("en-IN")}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </main>
    </div>
  );
}

